<template>
    <v-form ref="form" v-model="valid" lazy-validation>
        <v-card class="co-card overflow-x-hidden">

            <!-- ══ Header ══ -->
            <div class="co-header">
                <div class="co-header-left">
                    <v-icon color="white" class="mr-2" size="20">mdi-office-building</v-icon>
                    <div>
                        <div class="co-header-title">Company Information</div>
                        <div v-if="form.name" class="co-header-sub">{{ form.name }}</div>
                    </div>
                </div>
                <div class="d-flex align-center gap-8">
                    <!-- View Tickets badge -->
                   
                    <v-btn icon small @click="closeDialog()" class="co-icon-btn">
                        <v-icon color="rgba(255,255,255,0.75)" size="20">mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>

            <v-card-text class="co-body pa-0">
                <div class="co-form-wrap px-5 pt-4 pb-2">

                    <!-- ══ Basic Info ══ -->
                    <div class="co-section-label">
                        <v-icon small color="#7eb3ff" class="mr-1">mdi-information-outline</v-icon>
                        Basic Information
                    </div>
                    <v-row no-gutters class="mb-1">
                        <v-col cols="12">
                            <div class="d-flex align-center" style="gap:8px;">
                                <v-text-field
                                    v-model="form.code"
                                    :disabled="disabled_view"
                                    label="Code (QR value)"
                                    :placeholder="actions === 'Submit' ? 'Leave blank to auto-generate' : ''"
                                    outlined dense dark color="#7eb3ff"
                                    class="co-field mb-0"
                                    hide-details="auto"
                                    maxlength="6"
                                    @input="form.code = form.code ? form.code.toUpperCase().replace(/[^A-Z0-9]/g,'') : ''"
                                ></v-text-field>
                            </div>
                        </v-col>
                        <v-col cols="12" class="mt-2 mt-5">
                            <v-text-field
                                v-model="form.name"
                                :disabled="disabled_view"
                                :rules="errors.required"
                                label="Company Name"
                                outlined dense dark color="#7eb3ff"
                                class="co-field"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                v-model="form.no_of_gifts"
                                :disabled="disabled_view"
                                type="number"
                                label="No. of Gifts"
                                outlined dense dark color="#7eb3ff"
                                class="co-field"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                v-model="form.max_pax"
                                :disabled="disabled_view"
                                type="number"
                                min="0"
                                label="Max Participants (leave blank for unlimited)"
                                prepend-inner-icon="mdi-account-group-outline"
                                outlined dense dark color="#7eb3ff"
                                class="co-field"
                                hint="Maximum number of guests that can register for this company"
                                persistent-hint
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <!-- ══ Winner Info (Update/View only) ══ -->
                    <template v-if="actions !== 'Submit' && (form.ticket_num || form.prize_name)">
                        <div class="co-section-label mt-1">
                            <v-icon small color="#ffd54f" class="mr-1">mdi-trophy-outline</v-icon>
                            Winner Info
                        </div>
                        <v-row no-gutters class="mb-1">
                            <v-col cols="5" class="pr-2">
                                <v-text-field
                                    v-model="form.ticket_num"
                                    :disabled="disabled_view"
                                    label="Winning Ticket #"
                                    outlined dense dark color="#ffd54f"
                                    readonly class="co-field"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="7">
                                <v-text-field
                                    v-model="form.prize_name"
                                    :disabled="disabled_view"
                                    label="Prize Name"
                                    outlined dense dark color="#ffd54f"
                                    readonly class="co-field"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </template>

                    <!-- ══ Win Probability (Update/View only) ══ -->
                    <template v-if="actions !== 'Submit'">
                        <div class="co-section-label mt-1">
                            <v-icon small color="#ce93d8" class="mr-1">mdi-chart-pie</v-icon>
                            Win Probability
                            <span class="co-prob-note ml-2">(based on included tickets)</span>
                        </div>

                        <div class="co-prob-row mb-2">
                            <!-- Normal Prize card -->
                            <div class="co-prob-card co-prob-card--normal" :class="{ 'co-prob-card--won': probability.normal.already_won }">
                                <div class="co-prob-card-label">
                                    Normal Prize
                                    <v-chip v-if="probability.normal.already_won" x-small color="#4caf50" text-color="white" class="ml-1 co-won-chip">
                                        <v-icon x-small left>mdi-trophy</v-icon>Already Won
                                    </v-chip>
                                </div>
                                <div class="co-prob-gauge-wrap">
                                    <v-progress-circular
                                        :value="probability.normal.already_won ? 100 : probability.normal.probability"
                                        :size="72"
                                        :width="7"
                                        :color="probability.normal.already_won ? '#4caf50' : '#4fc3f7'"
                                        rotate="-90"
                                    >
                                        <span class="co-prob-pct-lg" :style="{ color: probability.normal.already_won ? '#4caf50' : '#4fc3f7' }">
                                            {{ probability.normal.already_won ? '✓' : probability.normal.probability + '%' }}
                                        </span>
                                    </v-progress-circular>
                                </div>
                                <div class="co-prob-tickets mt-2">
                                    <v-icon size="11" :color="probability.normal.already_won ? '#4caf50' : '#4fc3f7'">mdi-ticket-outline</v-icon>
                                    <strong :style="{ color: probability.normal.already_won ? '#4caf50' : '#4fc3f7' }">{{ probability.normal.company_tickets }}</strong>
                                    <span style="color:rgba(200,210,255,0.4)"> / {{ probability.normal.total_tickets }}</span>
                                    <span v-if="!probability.normal.already_won" style="color:rgba(200,210,255,0.3);font-size:0.65rem;" class="ml-1">(active)</span>
                                </div>
                                <v-progress-linear
                                    :value="probability.normal.already_won ? 100 : probability.normal.probability"
                                    :color="probability.normal.already_won ? '#4caf50' : '#4fc3f7'"
                                    background-color="rgba(79,195,247,0.12)"
                                    rounded height="4"
                                    class="mt-2"
                                ></v-progress-linear>
                            </div>

                            <!-- Major Prize card -->
                            <div class="co-prob-card co-prob-card--major" :class="{ 'co-prob-card--unconfigured': !probability.major.configured, 'co-prob-card--won': probability.major.already_won }">
                                <div class="co-prob-card-label">
                                    Major Prize
                                    <v-icon v-if="probability.major.configured && !probability.major.already_won" size="12" color="#ffa726" class="ml-1">mdi-star</v-icon>
                                    <v-chip v-if="probability.major.already_won" x-small color="#4caf50" text-color="white" class="ml-1 co-won-chip">
                                        <v-icon x-small left>mdi-trophy</v-icon>Already Won
                                    </v-chip>
                                </div>
                                <div class="co-prob-gauge-wrap">
                                    <v-progress-circular
                                        :value="probability.major.already_won ? 100 : (probability.major.configured ? probability.major.probability : 0)"
                                        :size="72"
                                        :width="7"
                                        :color="probability.major.already_won ? '#4caf50' : '#ffa726'"
                                        rotate="-90"
                                    >
                                        <span class="co-prob-pct-lg" :style="{ color: probability.major.already_won ? '#4caf50' : '#ffa726' }">
                                            <template v-if="probability.major.already_won">✓</template>
                                            <template v-else-if="probability.major.configured">{{ probability.major.probability }}%</template>
                                            <template v-else>—</template>
                                        </span>
                                    </v-progress-circular>
                                </div>
                                <div class="co-prob-tickets mt-2">
                                    <template v-if="probability.major.configured">
                                        <v-icon size="11" :color="probability.major.already_won ? '#4caf50' : '#ffa726'">
                                            {{ probability.major.already_won ? 'mdi-trophy' : 'mdi-star-outline' }}
                                        </v-icon>
                                        <strong :style="{ color: probability.major.already_won ? '#4caf50' : '#ffa726' }">{{ probability.major.company_tickets }}</strong>
                                        <span style="color:rgba(200,210,255,0.4)"> / {{ probability.major.total_tickets }}</span>
                                        <span v-if="!probability.major.already_won" style="color:rgba(200,210,255,0.3);font-size:0.65rem;" class="ml-1">(active)</span>
                                    </template>
                                    <span v-else style="color:rgba(200,210,255,0.3);font-size:0.68rem;">No major prizes configured</span>
                                </div>
                                <v-progress-linear
                                    :value="probability.major.already_won ? 100 : (probability.major.configured ? probability.major.probability : 0)"
                                    :color="probability.major.already_won ? '#4caf50' : '#ffa726'"
                                    background-color="rgba(255,167,38,0.12)"
                                    rounded height="4"
                                    class="mt-2"
                                ></v-progress-linear>
                            </div>
                        </div>

                        <!-- Per-group breakdown (collapsible) -->
                        <v-expansion-panels v-if="probability.by_group.length > 0" flat class="co-prob-expand mb-3">
                            <v-expansion-panel class="co-prob-expand-panel">
                                <v-expansion-panel-header class="co-prob-expand-header pa-2">
                                    <span><v-icon small color="#7eb3ff" class="mr-1">mdi-chart-bar</v-icon> Per Raffle Group Breakdown</span>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content class="co-prob-expand-content pa-0">
                                    <div
                                        v-for="grp in probability.by_group"
                                        :key="grp.raffle_group_id"
                                        class="co-prob-group-row"
                                    >
                                        <span class="co-prob-group-name">{{ getRaffleGroupName(grp.raffle_group_id) }}</span>
                                        <div class="co-prob-group-bar-wrap">
                                            <v-progress-linear
                                                :value="grp.probability"
                                                color="#7eb3ff"
                                                background-color="rgba(126,179,255,0.12)"
                                                rounded height="6"
                                            ></v-progress-linear>
                                        </div>
                                        <span class="co-prob-group-pct">{{ grp.probability }}%</span>
                                        <span class="co-prob-group-tickets">{{ grp.company_tickets }}/{{ grp.total_tickets }}</span>
                                    </div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>

                        <div v-if="!form.include_raffle" class="co-prob-excluded mb-3">
                            <v-icon size="14" color="rgba(200,210,255,0.4)" class="mr-1">mdi-information-outline</v-icon>
                            This company is not included in the raffle — probabilities show 0%.
                        </div>
                    </template>

                    <!-- ══ Raffle Settings ══ -->
                    <div class="co-section-label mt-1">
                        <v-icon small color="#7eb3ff" class="mr-1">mdi-ticket-confirmation-outline</v-icon>
                        Raffle Settings
                    </div>
                    <div class="co-raffle-row mb-2">
                        <v-checkbox
                            v-model="form.include_raffle"
                            :disabled="disabled_view"
                            :true-value="1"
                            :false-value="0"
                            hide-details
                            color="#7eb3ff"
                            dense
                            class="mt-0 pt-0 co-checkbox"
                        >
                            <template v-slot:label>
                                <span class="co-checkbox-label">Include in Raffle</span>
                            </template>
                        </v-checkbox>
                        <template v-if="form.include_raffle">
                            <span class="co-tickets-readonly ml-3">
                                <v-icon small color="#7eb3ff">mdi-ticket-outline</v-icon>
                                {{ totalTickets }} tickets total
                            </span>
                        </template>
                        <v-tooltip bottom color="#1565c0" v-if="actions !== 'Submit'">
                            <template v-slot:activator="{ on, attrs }">
                                <v-badge
                                    :value="form.included_ticket_count > 0"
                                    :content="form.included_ticket_count"
                                    color="#f57c00"
                                    overlap
                                >
                                    <v-btn icon small v-bind="attrs" v-on="on" @click="ticketDialog(true)" class="co-icon-btn">
                                        <v-icon color="white" size="20">mdi-ticket-account</v-icon>
                                    </v-btn>
                                </v-badge>
                            </template>
                            <span>View Company Tickets</span>
                        </v-tooltip>
                    </div>

                    <!-- Per-group raffle participation -->
                    <template v-if="form.include_raffle">
                        <div class="co-group-table-wrap mb-3">
                            <div class="co-group-table-header">
                                <span>Raffle Group</span>
                                <span>No. of Tickets</span>
                                <span>Generated</span>
                                <span v-if="actions !== 'View'">Actions</span>
                            </div>
                            <div
                                v-for="(grp, gi) in form.company_raffle_groups"
                                :key="gi"
                                class="co-group-table-row"
                            >
                                <v-autocomplete
                                    v-model="grp.raffle_group_id"
                                    :items="availableGroupsFor(gi)"
                                    item-text="name"
                                    item-value="id"
                                    outlined dense dark color="#7eb3ff"
                                    hide-details
                                    :disabled="disabled_view"
                                    placeholder="Select group"
                                    class="co-field co-grp-select"
                                ></v-autocomplete>
                                <v-text-field
                                    v-model="grp.no_of_tickets"
                                    type="number" min="0"
                                    outlined dense dark color="#7eb3ff"
                                    hide-details
                                    :disabled="disabled_view"
                                    class="co-field co-grp-tickets"
                                ></v-text-field>
                                <span class="co-grp-staged-count">
                                    <v-icon x-small :color="(existingCountForGroup(grp.raffle_group_id) + stagedCountForGroup(grp.raffle_group_id)) > 0 ? '#4caf50' : 'rgba(200,210,255,0.3)'">mdi-ticket-outline</v-icon>
                                    {{ existingCountForGroup(grp.raffle_group_id) + stagedCountForGroup(grp.raffle_group_id) }}
                                    <span style="color:rgba(200,210,255,0.3)"> / {{ grp.no_of_tickets }}</span>
                                </span>
                                <div v-if="actions !== 'View'" class="co-grp-actions">
                                    <v-tooltip bottom color="#1565c0">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                depressed x-small
                                                class="co-gen-btn mr-1"
                                                :disabled="!grp.raffle_group_id"
                                                v-bind="attrs" v-on="on"
                                                @click="openGroupGenerate(gi)"
                                            >
                                                <v-icon x-small>mdi-cog-outline</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Generate Tickets</span>
                                    </v-tooltip>
                                    <v-tooltip bottom color="#2e7d32">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                depressed x-small
                                                class="co-import-btn mr-1"
                                                :disabled="!grp.raffle_group_id"
                                                v-bind="attrs" v-on="on"
                                                @click="triggerImport(gi)"
                                            >
                                                <v-icon x-small>mdi-ticket-outline</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Import Ticket Numbers</span>
                                    </v-tooltip>
                                 
                                </div>
                            </div>
                            <div v-if="form.company_raffle_groups.length === 0" class="co-group-empty">
                                No raffle groups added. Click "Add Group" to add one.
                            </div>
                            <v-btn
                                v-if="actions !== 'View'"
                                depressed x-small
                                class="co-add-group-btn mt-2"
                                @click="addRaffleGroup()"
                            >
                                <v-icon x-small left>mdi-plus</v-icon>
                                Add Group
                            </v-btn>

                        </div>

                        <!-- ── Ticket Import Dialog ──────────────────────────────────── -->
                        <v-dialog v-model="ticketImportDialog" max-width="520" scrollable>
                            <v-card dark style="background:#1e2a3a;border:1px solid rgba(126,179,255,0.2)">
                                <v-card-title class="pb-1" style="font-size:1rem;color:#7eb3ff">
                                    <v-icon left color="#7eb3ff">mdi-ticket-plus-outline</v-icon>
                                    Import Tickets
                                    <span v-if="importGroupIndex !== null && form.company_raffle_groups[importGroupIndex]"
                                          style="margin-left:6px;color:#ffd54f;font-size:0.85rem">
                                        — {{ getRaffleGroupName(form.company_raffle_groups[importGroupIndex].raffle_group_id) }}
                                    </span>
                                    <v-spacer/>
                                    <v-btn icon x-small @click="ticketImportDialog = false">
                                        <v-icon small>mdi-close</v-icon>
                                    </v-btn>
                                </v-card-title>

                                <v-card-text class="pb-2">
                                    <!-- Paste area -->
                                    <p style="font-size:0.8rem;color:rgba(200,210,255,0.6);margin-bottom:6px">
                                        Paste or type ticket numbers. Separate with <b>commas</b>, <b>spaces</b>, or <b>new lines</b> — they will split automatically into individual rows.
                                    </p>
                                    <v-textarea
                                        v-model="ticketImportInput"
                                        outlined dense dark
                                        color="#7eb3ff"
                                        label="Paste tickets here (e.g. 0001, 0002, 0003)"
                                        placeholder="0001, 0002, 0003"
                                        rows="3"
                                        auto-grow
                                        hide-details
                                        class="mb-3"
                                        @input="parseImportInput"
                                    ></v-textarea>

                                    <!-- Parsed rows -->
                                    <div v-if="ticketImportRows.length > 0">
                                        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
                                            <span style="font-size:0.8rem;color:rgba(200,210,255,0.7)">
                                                <v-icon x-small color="#4caf50">mdi-check-circle-outline</v-icon>
                                                {{ ticketImportRows.length }} ticket(s) ready
                                            </span>
                                            <v-btn x-small text color="#ef5350" @click="ticketImportRows = []; ticketImportInput = ''">
                                                <v-icon x-small left>mdi-delete-sweep</v-icon>Clear all
                                            </v-btn>
                                        </div>
                                        <div class="ti-rows-wrap">
                                            <div
                                                v-for="(row, ri) in ticketImportRows"
                                                :key="ri"
                                                class="ti-row"
                                            >
                                                <span class="ti-row-num">{{ ri + 1 }}</span>
                                                <v-text-field
                                                    v-model="ticketImportRows[ri]"
                                                    outlined dense dark
                                                    color="#7eb3ff"
                                                    hide-details
                                                    class="ti-row-input"
                                                    @keydown.enter="addImportRow"
                                                    @keydown.comma.prevent="splitOnCommaKeydown(ri)"
                                                    @paste.prevent="onRowPaste(ri, $event)"
                                                ></v-text-field>
                                                <v-btn icon x-small color="#ef5350" @click="ticketImportRows.splice(ri, 1)">
                                                    <v-icon x-small>mdi-close</v-icon>
                                                </v-btn>
                                            </div>
                                            <!-- Add new row button -->
                                            <v-btn
                                                x-small text color="#7eb3ff"
                                                class="mt-1"
                                                @click="addImportRow"
                                            >
                                                <v-icon x-small left>mdi-plus</v-icon>Add row
                                            </v-btn>
                                        </div>
                                    </div>
                                    <div v-else style="font-size:0.8rem;color:rgba(200,210,255,0.35);text-align:center;padding:10px 0">
                                        No tickets yet — paste or type above.
                                    </div>
                                </v-card-text>

                                <v-card-actions class="pt-0 pb-3 px-4">
                                    <v-spacer/>
                                    <v-btn small text @click="ticketImportDialog = false">Cancel</v-btn>
                                    <v-btn
                                        small depressed
                                        color="#2e7d32"
                                        class="white--text"
                                        :disabled="ticketImportRows.filter(r => r.trim()).length === 0"
                                        :loading="ticketImportProcessing"
                                        @click="processImportRows"
                                    >
                                        <v-icon x-small left>mdi-import</v-icon>
                                        Import {{ ticketImportRows.filter(r => r.trim()).length }} ticket(s)
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>

                        <!-- Staged tickets preview (all groups) -->
                        <div v-if="form.temp_company_tickets.length > 0" class="co-staged-wrap mb-3">
                            <div class="co-staged-header">
                                <v-icon small color="#4caf50">mdi-check-circle-outline</v-icon>
                                {{ form.temp_company_tickets.length }} ticket(s) staged for save
                            </div>
                            <div class="co-staged-chips co-staged-scroll">
                                <v-chip
                                    v-for="(ticket, idx) in form.temp_company_tickets"
                                    :key="idx"
                                    x-small
                                    class="co-ticket-chip ma-1"
                                    color="rgba(79,195,247,0.15)"
                                    text-color="#4fc3f7"
                                >
                                    {{ ticket.ticket_num }}
                                    <span class="co-ticket-group ml-1">{{ getRaffleGroupName(ticket.raffle_group_id) }}</span>
                                </v-chip>
                            </div>
                        </div>
                    </template>

                </div>
            </v-card-text>

            <!-- ══ Actions ══ -->
            <div class="co-actions">
                <v-btn
                    v-if="actions !== 'View'"
                    depressed
                    :disabled="disabled_view"
                    class="co-submit-btn"
                    @click="actions === 'Submit' ? addCompany() : updateCompany()"
                >
                    <v-icon left small>{{ actions === 'Submit' ? 'mdi-plus' : 'mdi-content-save-outline' }}</v-icon>
                    {{ actions }}
                </v-btn>
            </div>

            <!-- ══ Company Tickets Dialog ══ -->
            <v-dialog v-model="company_tickets.dialog" width="65%" :retain-focus="false">
                <v-card class="co-sub-card">
                    <div class="co-sub-header">
                        <div>
                            <v-icon color="white" class="mr-2" size="18">mdi-ticket-account</v-icon>
                            Company Tickets
                            <v-chip x-small class="ml-2" color="rgba(255,255,255,0.15)" text-color="white">
                                {{ filteredCompanyTickets().length }}
                            </v-chip>
                        </div>
                        <v-btn icon small @click="ticketDialog(false)" class="co-icon-btn">
                            <v-icon color="rgba(255,255,255,0.7)" size="18">mdi-close</v-icon>
                        </v-btn>
                    </div>
                    <v-card-text class="pa-4">
                        <v-row no-gutters class="mb-3">
                            <v-col cols="4">
                                <v-autocomplete
                                    v-model="filters.raffle_group_id"
                                    :items="GET_RAFFLE_GROUP_SELECTION"
                                    label="Filter by Group"
                                    item-text="name"
                                    item-value="id"
                                    outlined dense dark color="#7eb3ff"
                                    clearable hide-details
                                    class="co-field"
                                ></v-autocomplete>
                            </v-col>
                            <v-spacer></v-spacer>
                            <template v-if="actions === 'Update'">
                                <v-btn small depressed color="#f57c00" class="mr-2 co-action-btn" @click="includeExcludeTicket(1)">
                                    <v-icon small left>mdi-check</v-icon> Include Selected
                                </v-btn>
                                <v-btn small depressed color="#c62828" class="co-action-btn" @click="includeExcludeTicket(2)">
                                    <v-icon small left>mdi-cancel</v-icon> Exclude Selected
                                </v-btn>
                            </template>
                        </v-row>
                        <v-data-table
                            v-model="company_tickets.included_tickets"
                            :headers="company_tickets.headers"
                            :items="filteredCompanyTickets()"
                            :items-per-page="-1"
                            :show-select="actions === 'Update'"
                            hide-default-footer
                            fixed-header
                            height="48vh"
                            class="co-table"
                        >
                            <template v-slot:[`item.included_in_raffle`]="{item}">
                                <v-chip x-small :color="item.included_in_raffle ? 'rgba(76,175,80,0.2)' : 'rgba(244,67,54,0.2)'" :text-color="item.included_in_raffle ? '#81c784' : '#e57373'">
                                    {{ item.included_in_raffle ? 'Included' : 'Excluded' }}
                                </v-chip>
                            </template>
                            <template v-slot:[`item.action`]="{item, index}">
                                <v-btn
                                    v-if="actions === 'Update'"
                                    icon x-small color="#ef5350"
                                    @click="deleteTicket(item, index)"
                                >
                                    <v-icon size="16">mdi-delete-outline</v-icon>
                                </v-btn>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
            </v-dialog>

            <!-- ══ Generate Ticket By Group Dialog ══ -->
            <v-dialog v-model="raffle_group.dialog" width="60%" :retain-focus="false" persistent>
                <v-card class="co-sub-card">
                    <div class="co-sub-header">
                        <div>
                            <v-icon color="white" class="mr-2" size="18">mdi-ticket-confirmation-outline</v-icon>
                            Generate Tickets — {{ getRaffleGroupName(generate_raffle_group_id) }}
                        </div>
                        <v-btn icon small @click="closeRaffleTicketDialog()" class="co-icon-btn">
                            <v-icon color="rgba(255,255,255,0.7)" size="18">mdi-close</v-icon>
                        </v-btn>
                    </div>
                    <v-card-text class="pa-4">
                        <!-- Row 1: Count + ticket range -->
                        <v-row no-gutters class="mb-2" align="center">
                            <v-col cols="3" class="pr-2">
                                <v-text-field
                                    v-model="no_of_tickets_temp"
                                    :disabled="disabled_view"
                                    label="Count"
                                    type="number" min="0"
                                    outlined dense dark color="#7eb3ff"
                                    hide-details
                                    class="co-field"
                                    @input="changeTicketTo()"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="2" class="pr-2">
                                <v-text-field
                                    v-model="maxTicketNum"
                                    :disabled="disabled_view"
                                    label="Next #"
                                    outlined dense dark color="#7eb3ff"
                                    hide-details readonly
                                    class="co-field"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="3" v-if="!form.is_random && actions === 'Update'" class="pr-2">
                                <v-text-field
                                    v-model="form.ticket_from"
                                    :disabled="disabled_view"
                                    label="From"
                                    outlined dense dark color="#7eb3ff"
                                    hide-details
                                    class="co-field"
                                    @input="changeTicketTo()"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="3" v-if="!form.is_random && actions === 'Update'">
                                <v-text-field
                                    v-model="form.ticket_to"
                                    :disabled="disabled_view"
                                    label="To"
                                    outlined dense dark color="#7eb3ff"
                                    hide-details readonly
                                    class="co-field"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <!-- Row 2: Generate button + Random toggle -->
                        <v-row no-gutters align="center" class="mb-3" v-if="actions !== 'View'">
                            <v-btn depressed class="co-gen-btn mr-3" @click="generateTickets()">
                                <v-icon small left>mdi-cog-outline</v-icon>
                                Generate Tickets
                            </v-btn>
                            <v-checkbox
                                v-model="form.is_random"
                                color="#7eb3ff"
                                hide-details dense
                                class="mt-0 pt-0 co-checkbox"
                                @click="generateTickets()"
                            >
                                <template v-slot:label>
                                    <span class="co-checkbox-label">Random Numbers</span>
                                </template>
                            </v-checkbox>
                        </v-row>

                        <!-- Generated tickets — scrollable, show all -->
                        <div v-if="form.temp_company_tickets_group.length > 0" class="co-staged-wrap">
                            <div class="co-staged-header">
                                <v-icon small color="#4caf50">mdi-check-circle-outline</v-icon>
                                {{ form.temp_company_tickets_group.length }} ticket(s) generated — click Save to confirm
                            </div>
                            <div class="co-staged-chips co-staged-scroll">
                                <v-chip
                                    v-for="(ticket, idx) in form.temp_company_tickets_group"
                                    :key="idx"
                                    x-small
                                    class="co-ticket-chip ma-1"
                                    color="rgba(79,195,247,0.15)"
                                    text-color="#4fc3f7"
                                >
                                    {{ ticket.ticket_num }}
                                </v-chip>
                            </div>
                        </div>
                    </v-card-text>
                    <div class="co-actions" v-if="actions !== 'View'">
                        <v-btn depressed class="co-submit-btn" @click="saveGroupTickets()">
                            <v-icon left small>mdi-content-save-outline</v-icon>
                            Save
                        </v-btn>
                    </div>
                </v-card>
            </v-dialog>

        </v-card>
    </v-form>
</template>

<script>
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { mapGetters } from "vuex";
export default {
    data() {
        return {
            valid: false,
            overlay: false,
            disabled_view: false,
            errors: {
                required: [(v) => !!v || "Field is required"],
            },
            form: {},
            isHover: false,
            company_tickets: {
                dialog: false,
                headers: [
                    { text: 'Ticket#',            align: 'start',  value: 'ticket_num' },
                    { text: 'Raffle Group',        align: 'center', value: 'raffle_group_name' },
                    { text: 'Prize',               align: 'center', value: 'prize_name' },
                    { text: 'Included in Raffle',  align: 'center', value: 'included_in_raffle' },
                    { text: 'Action',              align: 'center', value: 'action' },
                ],
                items: [],
                included_tickets: []
            },
            raffle_group: {
                dialog: false,
                id: null,
                group_index: null,
            },
            importGroupIndex: null,
            ticketImportDialog: false,
            ticketImportInput: '',
            ticketImportRows: [],
            ticketImportProcessing: false,
            maxTicketNum: null,
            settings: {
                ticket_length: 0,
                disable_company_editing: 0,
            },
            filters: {
                raffle_group_id: null
            },
            generate_raffle_group_id: null,
            no_of_tickets_temp: 0,
            hasLoadedCompanyData: false,
            probability: {
                normal: { company_tickets: 0, total_tickets: 0, probability: 0, already_won: false },
                major:  { company_tickets: 0, total_tickets: 0, probability: 0, configured: false, already_won: false },
                by_group: [],
            },
            probabilityLoading: false,
            generatingUniqueCode: false,
        };
    },
    computed: {
        ...mapGetters([
            "ACTION",
            "DIALOGS",
            "GET_DIALOG_VIEW",
            "GET_EVENT_ID",
            "GET_RAFFLE_GROUP_SELECTION"
        ]),
        totalTickets() {
            return (this.form.company_raffle_groups || []).reduce((sum, g) => sum + (parseInt(g.no_of_tickets) || 0), 0);
        },
        actions() {
            if (this.ACTION == "Submit") {
                this.disabled_view = false;
                this.isViewBtn = false;
                this.getMaxTicketNum();
                return "Submit";
            }
            if (this.ACTION === "Update" || this.ACTION === "View") {
                if (this.ACTION === "Update" && this.settings.disable_company_editing === 1) {
                    this.$nextTick(() => {
                        Swal.fire({
                            title: 'Company Editing Disabled',
                            text: 'Company editing is currently not available. Please contact your administrator.',
                            icon: 'info',
                            confirmButtonText: 'OK',
                            allowOutsideClick: false,
                        }).then(() => {
                            this.closeDialog();
                        });
                    });
                    return "Update";
                }
                this.disabled_view = this.ACTION === "View";
                this.isViewBtn = true;
                if (!this.hasLoadedCompanyData) {
                    this.getCompanyData();
                    this.hasLoadedCompanyData = true;
                }
                return this.ACTION;
            }
        },
    },
    created() {
        this.initialForm();
    },
    mounted() {
        this.getRaffleSettings();
        this.setDefaults();
    },
    methods: {
        async getRaffleSettings() {
            let payload = {
                type: 'Raffle',
                url: 'moduleSettings',
                event_id: this.GET_EVENT_ID
            };
            await this.$store.dispatch("urlGet", payload).then((res) => {
                res.forEach(setting => {
                    Object.keys(this.settings).forEach(key => {
                        if (key === setting.parameter) {
                            this.settings[key] = key === 'disable_company_editing'
                                ? parseInt(setting.value)
                                : parseFloat(setting.value);
                        }
                    });
                });
            });
        },
        initialForm() {
            this.form = {
                code: '',
                unique_code: '',
                name: '',
                no_of_tickets: 0,
                include_raffle: 0,
                company_raffle_groups: [],
                temp_company_tickets: [],
                temp_company_tickets_group: [],
                ticket_from: null,
                ticket_to: null,
                included_ticket_count: 0,
                is_random: false,
                no_of_gifts: 0,
                max_pax: null,
            };
            this.probability = {
                normal: { company_tickets: 0, total_tickets: 0, probability: 0, already_won: false },
                major:  { company_tickets: 0, total_tickets: 0, probability: 0, configured: false, already_won: false },
                by_group: [],
            };
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },
        closeDialog() {
            this.$store.commit("DIALOG", false);
            this.$store.commit('ACTION', null);
            this.hasLoadedCompanyData = false;
        },
        async addCompany() {
            this.$refs.form.validate();
            if (this.form.name) {
                let payload = {
                    url: "companyRoute",
                    ...this.form,
                    event_id: this.GET_EVENT_ID
                };
                try {
                    let response = await this.$store.dispatch('urlPost', payload);
                    Swal.fire(response.icon.toUpperCase(), response.message, response.icon);
                    this.$store.commit('NEW_MASTER_DATA', true);
                    this.$store.commit("DIALOG", false);
                } catch (error) {
                    if (error.response && error.response.status == 401) {
                        Swal.fire({ title: 'Unauthenticated!', text: '', icon: 'error', allowOutsideClick: false }).then((result) => {
                            if (result.isConfirmed) {
                                localStorage.removeItem("token");
                                window.location.reload();
                                this.$router.push('/');
                            }
                        });
                    }
                }
            } else {
                Swal.fire({ text: 'Company Name is required.', icon: 'warning', allowOutsideClick: false });
            }
        },
        async updateCompany() {
            if (this.settings.disable_company_editing === 1) {
                Swal.fire({
                    title: 'Company Editing Disabled',
                    text: 'Company editing is currently not available. Please contact your administrator.',
                    icon: 'info',
                    confirmButtonText: 'OK',
                    allowOutsideClick: false,
                });
                return;
            }
            if (this.form.include_raffle == 0 && (this.GET_DIALOG_VIEW.include_raffle != this.form.include_raffle && this.form.company_tickets.length > 0)) {
                Swal.fire({
                    title: 'Are you sure you want to update?',
                    text: 'Company not included in raffle! Tickets will be deleted.',
                    icon: 'question',
                    showConfirmButton: true,
                    confirmButtonText: 'Confirm',
                    showCancelButton: true,
                    showCloseButton: true,
                    reverseButtons: true,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.triggerUpdateNow();
                    }
                });
            } else {
                this.triggerUpdateNow();
            }
        },
        async triggerUpdateNow() {
            let payload = {
                url: "companyRoute",
                id: this.form.id,
                ...this.form,
                event_id: this.GET_EVENT_ID
            };
            try {
                const response = await this.$store.dispatch("urlPut", payload);
                Swal.fire(response.icon.toUpperCase(), response.message, response.icon);
                this.$store.commit('NEW_MASTER_DATA', true);
                this.closeDialog();
            } catch (error) {
                const data = error?.response?.data;
                if (error?.response?.status === 403 && data?.blocked) {
                    Swal.fire({
                        title: 'Company Editing Disabled',
                        text: data.message,
                        icon: 'info',
                        confirmButtonText: 'OK',
                        allowOutsideClick: false,
                    }).then(() => {
                        this.closeDialog();
                    });
                } else if (error?.response?.status === 401) {
                    // handled by store
                } else {
                    Swal.fire('Error', 'Update failed. Please try again.', 'error');
                }
            }
        },
        async getCompanyData() {
            this.getMaxTicketNum();
            this.form.id                    = this.GET_DIALOG_VIEW.id;
            this.form.code                  = this.GET_DIALOG_VIEW.code;
            this.form.unique_code           = this.GET_DIALOG_VIEW.unique_code ?? '';
            this.form.name                  = this.GET_DIALOG_VIEW.name;
            this.form.no_of_tickets         = this.GET_DIALOG_VIEW.no_of_tickets;
            this.form.include_raffle        = parseInt(this.GET_DIALOG_VIEW.include_raffle);
            this.form.company_tickets       = this.GET_DIALOG_VIEW.company_tickets;
            this.form.is_random             = this.GET_DIALOG_VIEW.is_random;
            this.form.no_of_gifts           = this.GET_DIALOG_VIEW.no_of_gifts;
            this.form.max_pax               = this.GET_DIALOG_VIEW.max_pax ?? null;
            this.form.included_ticket_count = this.GET_DIALOG_VIEW.company_tickets
                ? this.GET_DIALOG_VIEW.company_tickets.filter((x) => x.included_in_raffle == 1).length
                : 0;
            // Load per-group raffle participation
            this.form.company_raffle_groups = this.GET_DIALOG_VIEW.company_raffle_groups
                ? this.GET_DIALOG_VIEW.company_raffle_groups.map(g => ({
                    raffle_group_id: g.raffle_group_id,
                    no_of_tickets:   g.no_of_tickets,
                  }))
                : [];
            if (this.GET_DIALOG_VIEW.raffle_prize_details) {
                this.form.ticket_num  = this.GET_DIALOG_VIEW.raffle_prize_details.ticket_num;
                this.form.prize_name  = this.GET_DIALOG_VIEW.raffle_prize_details.prize_name;
            }
            this.form.new_ticket         = false;
            this.form.last_ticket_number = this.GET_DIALOG_VIEW.last_ticket_number;

            // Load probability after company data is ready (always, not just when included)
            this.loadProbability();
        },
        async loadProbability() {
            this.probabilityLoading = true;
            try {
                const payload = {
                    url: 'getCompanyProbability',
                    company_id: this.form.id,
                    event_id: this.GET_EVENT_ID,
                };
                const res = await this.$store.dispatch('urlPost', payload);
                this.probability.normal   = res.normal;
                this.probability.major    = res.major;
                this.probability.by_group = res.by_group;
            } catch (err) {
                console.error('Could not load probability', err);
            } finally {
                this.probabilityLoading = false;
            }
        },
        generateCodeLocal() {
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
            let code = '';
            for (let i = 0; i < 6; i++) {
                code += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            this.form.code = code;
        },

        generateUniqueCodeLocal() {
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
            let code = '';
            for (let i = 0; i < 6; i++) {
                code += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            this.form.unique_code = code;
        },

        async generateUniqueCode() {
            if (!this.form.id) return;
            this.generatingUniqueCode = true;
            try {
                const payload = {
                    url: 'generateCompanyUniqueCode',
                    company_id: this.form.id,
                };
                const res = await this.$store.dispatch('urlPost', payload);
                this.form.unique_code = res.unique_code;
                this.$store.commit('NEW_MASTER_DATA', true);
            } catch (err) {
                Swal.fire('Error', 'Failed to generate unique code.', 'error');
            } finally {
                this.generatingUniqueCode = false;
            }
        },

        async generateTickets() {
            if (!this.generate_raffle_group_id) {
                Swal.fire({ title: 'Please select Raffle Group', text: '', icon: 'warning' });
                return false;
            }

            this.form.temp_company_tickets_group = [];

            if (!this.no_of_tickets_temp || parseInt(this.no_of_tickets_temp) == 0) {
                Swal.fire({ title: 'Cannot generate 0 ticket!', text: 'Please indicate No. of Ticket', icon: 'warning' });
                return false;
            }

            // Per-group quota check
            const grpRow = (this.form.company_raffle_groups || []).find(g => g.raffle_group_id == this.generate_raffle_group_id);
            if (grpRow) {
                const quota       = parseInt(grpRow.no_of_tickets) || 0;
                const existing    = this.existingCountForGroup(this.generate_raffle_group_id);
                const staged      = this.stagedCountForGroup(this.generate_raffle_group_id);
                const toGenerate  = this.form.is_random
                    ? parseInt(this.no_of_tickets_temp)
                    : (parseInt(this.form.ticket_to) - parseInt(this.form.ticket_from) + 1);
                if (existing + staged + toGenerate > quota) {
                    Swal.fire({ title: 'Exceeds group quota!', text: `This group has a quota of ${quota} ticket(s). Already generated: ${existing + staged}.`, icon: 'warning' });
                    return false;
                }
            }

            const toGenerate = this.form.is_random
                ? parseInt(this.no_of_tickets_temp)
                : (parseInt(this.form.ticket_to) - parseInt(this.form.ticket_from) + 1);

            if (toGenerate <= 0) {
                Swal.fire({ title: 'No tickets to generate!', text: 'Check the ticket range.', icon: 'warning' });
                return false;
            }

            const canGenerate = this.form.is_random ? true : await this.checkTicketRange();

            if (canGenerate) {
                const stagedTicketNums = (this.form.temp_company_tickets || []).map(ticket => ticket.ticket_num);
                let payload = {
                    url: 'generateCompanyTicket',
                    ...this.form,
                    raffle_group_id: this.generate_raffle_group_id,
                    event_id: this.GET_EVENT_ID,
                    staged_ticket_nums: stagedTicketNums,
                };
                try {
                    let response = await this.$store.dispatch('urlPost', payload);
                    if (response.data) {
                        this.form.temp_company_tickets_group = response.data;
                        this.form.new_ticket = true;
                    }
                } catch (error) {
                    if (error.response && error.response.status == 401) {
                        Swal.fire({ title: 'Unauthenticated!', text: '', icon: 'error', allowOutsideClick: false }).then((result) => {
                            if (result.isConfirmed) {
                                localStorage.removeItem("token");
                                window.location.reload();
                                this.$router.push('/');
                            }
                        });
                    }
                }
            }
        },
        ticketDialog(value) {
            this.company_tickets.dialog = value;
            if (value) {
                this.company_tickets.included_tickets = [];
            }
        },
        deleteTicket(item, index) {
            Swal.fire({
                title: "Delete",
                text: `Are you sure you want to delete ticket# ${item.ticket_num}?`,
                icon: 'question',
                showConfirmButton: true,
                confirmButtonText: 'Confirm',
                showCancelButton: true,
                reverseButtons: true,
            }).then((result) => {
                if (result.isConfirmed) {
                    let payload = { url: 'deleteTicket', id: item.id, event_id: this.GET_EVENT_ID };
                    this.$store.dispatch('urlDelete', payload).then((res) => {
                        this.form.company_tickets.splice(index, 1);
                        Swal.fire(res.message, "", res.icon);
                    });
                }
            });
        },
        async getMaxTicketNum(generate = 0) {
            let payload = {
                url: 'getMaxTicketNum',
                raffle_group_id: this.generate_raffle_group_id
            };
            await this.$store.dispatch('urlPost', payload).then((res) => {
                const nextNum      = parseInt(res);
                const no_of_ticket = parseInt(this.no_of_tickets_temp) || 0;
                this.maxTicketNum     = nextNum.toString().padStart(this.settings.ticket_length, "0");
                this.form.ticket_from = nextNum.toString().padStart(this.settings.ticket_length, "0");
                this.form.ticket_to   = no_of_ticket === 0
                    ? "0".padStart(this.settings.ticket_length, "0")
                    : (nextNum + no_of_ticket - 1).toString().padStart(this.settings.ticket_length, "0");
                if (generate) {
                    this.generateTickets();
                }
            });
        },
        async checkTicketRange() {
            return new Promise((resolve) => {
                let tickets = [];
                let ticket_to_generate = this.form.ticket_to - this.form.ticket_from;
                let current_ticket = this.form.ticket_from;
                for (let i = 0; i <= ticket_to_generate; i++) {
                    let ticketNumber = current_ticket.toString().padStart(this.settings.ticket_length, '0');
                    tickets.push(ticketNumber);
                    current_ticket++;
                }
                let payload = { url: 'checkCompanyTicketExists', company_tickets: tickets, company_id: this.form.id };
                this.$store.dispatch('urlPost', payload).then((res) => {
                    if (res.code == 200) {
                        resolve(true);
                    } else {
                        Swal.fire({
                            title: res.message,
                            html: `<div style="max-height:260px;overflow-y:auto;text-align:center;">${res.existing_tickets.join('<br>')}</div>`,
                            icon: 'warning',
                        });
                        resolve(false);
                    }
                });
            });
        },
        changeTicketTo() {
            let no_of_ticket = this.no_of_tickets_temp;
            this.form.ticket_to = (parseInt(this.form.ticket_from) + parseInt(no_of_ticket) - 1).toString().padStart(this.settings.ticket_length, "0");
        },
        includeExcludeTicket(action) {
            let included_ticket_ids = this.company_tickets.included_tickets.map(e => e.id);
            let payload = { url: 'includeExcludeTicket', included_ticket_ids, company_id: this.form.id, action };
            this.$store.dispatch('urlPost', payload).then((res) => {
                this.form.company_tickets = res.data;
                this.form.included_ticket_count = res.data.filter((x) => x.included_in_raffle == 1).length;
                this.$forceUpdate();
                Swal.fire(res.message, "", res.icon);
                // Refresh probability after include/exclude changes
                this.loadProbability();
            });
        },
        closeRaffleTicketDialog() {
            this.form.temp_company_tickets_group = [];
            this.raffle_group.dialog = false;
        },
        saveGroupTickets() {
            const merged = this.form.temp_company_tickets.concat(this.form.temp_company_tickets_group);
            const uniqueMap = new Map();
            merged.forEach(ticket => {
                const key = `${ticket.ticket_num}-${ticket.raffle_group_id}`;
                if (!uniqueMap.has(key)) uniqueMap.set(key, ticket);
            });
            this.form.temp_company_tickets = Array.from(uniqueMap.values());
            this.form.temp_company_tickets_group = [];
            this.raffle_group.dialog = false;
        },
        openGroupGenerate(groupIndex) {
            const grp = this.form.company_raffle_groups[groupIndex];
            if (!grp || !grp.raffle_group_id) return;

            const quota    = parseInt(grp.no_of_tickets) || 0;
            const existing = this.existingCountForGroup(grp.raffle_group_id);
            const staged   = this.stagedCountForGroup(grp.raffle_group_id);
            const remaining = Math.max(0, quota - existing - staged);

            if (remaining === 0 && quota > 0) {
                Swal.fire({ title: 'All tickets generated!', text: `This group already has all ${quota} ticket(s) generated. Increase the quota or delete existing tickets.`, icon: 'info' });
                return;
            }

            this.generate_raffle_group_id   = grp.raffle_group_id;
            this.raffle_group.group_index   = groupIndex;
            this.no_of_tickets_temp         = remaining;
            this.form.temp_company_tickets_group = [];

            // Compute ticket range
            let startFrom = parseInt(this.maxTicketNum) || 1;
            const stagedAll = (this.form.temp_company_tickets || []);
            if (stagedAll.length > 0) {
                const stagingMax = Math.max(...stagedAll.map(t => Number(t.ticket_num)));
                if (stagingMax >= startFrom) startFrom = stagingMax + 1;
            }
            this.form.ticket_from = startFrom.toString().padStart(this.settings.ticket_length || 6, "0");
            this.form.ticket_to   = remaining === 0
                ? "0".padStart(this.settings.ticket_length || 6, "0")
                : (startFrom + remaining - 1).toString().padStart(this.settings.ticket_length || 6, "0");

            this.raffle_group.dialog = true;
            if (remaining > 0) {
                this.generateTickets();
            }
        },
        addRaffleGroup() {
            this.form.company_raffle_groups.push({ raffle_group_id: null, no_of_tickets: 0 });
        },
        removeRaffleGroup(index) {
            const grp = this.form.company_raffle_groups[index];
            if (grp && grp.raffle_group_id) {
                // Remove any staged tickets for this group
                this.form.temp_company_tickets = this.form.temp_company_tickets.filter(
                    t => t.raffle_group_id != grp.raffle_group_id
                );
            }
            this.form.company_raffle_groups.splice(index, 1);
        },
        availableGroupsFor(currentIndex) {
            const usedIds = (this.form.company_raffle_groups || [])
                .filter((_, i) => i !== currentIndex)
                .map(g => g.raffle_group_id)
                .filter(Boolean);
            return (this.GET_RAFFLE_GROUP_SELECTION || []).filter(g => !usedIds.includes(g.id));
        },
        existingCountForGroup(raffle_group_id) {
            if (!raffle_group_id || !Array.isArray(this.form.company_tickets)) return 0;
            return this.form.company_tickets.filter(t => t.raffle_group_id == raffle_group_id).length;
        },
        stagedCountForGroup(raffle_group_id) {
            if (!raffle_group_id || !Array.isArray(this.form.temp_company_tickets)) return 0;
            return this.form.temp_company_tickets.filter(t => t.raffle_group_id == raffle_group_id).length;
        },
        filteredCompanyTickets() {
            if (this.form.company_tickets && this.form.company_tickets.length > 0) {
                return this.form.company_tickets.filter(e => !this.filters.raffle_group_id ? e : e.raffle_group_id == this.filters.raffle_group_id);
            }
            return [];
        },
        getRaffleGroupName(raffle_group_id) {
            return this.GET_RAFFLE_GROUP_SELECTION.find(e => e.id == raffle_group_id)?.name ?? '—';
        },
        // ── Import tickets via paste / manual input ──────────────────────────
        triggerImport(groupIndex) {
            this.importGroupIndex    = groupIndex;
            this.ticketImportInput   = '';
            this.ticketImportRows    = [];
            this.ticketImportDialog  = true;
        },
        parseImportInput() {
            const parts = this.ticketImportInput
                .split(/[\n,;\t ]+/)
                .map(s => s.trim())
                .filter(s => s.length > 0);
            this.ticketImportRows = parts;
        },
        onImportPaste(event) {
            const text = (event.clipboardData || window.clipboardData).getData('text').trim();
            // Clear first, then set on next tick so Vue picks up the change even if value is identical
            this.ticketImportInput = '';
            this.ticketImportRows  = [];
            this.$nextTick(() => {
                this.ticketImportInput = text;
                this.parseImportInput();
            });
        },

        onRowPaste(rowIndex, event) {
            const pasted = (event.clipboardData || window.clipboardData).getData('text').trim();
            const parts  = pasted.split(/[\n,;\t ]+/).map(s => s.trim()).filter(s => s.length > 0);
            if (parts.length > 1) {
                // Replace the current row and insert the rest after it
                this.ticketImportRows.splice(rowIndex, 1, ...parts);
            } else {
                this.$set(this.ticketImportRows, rowIndex, pasted);
            }
            this.ticketImportInput = this.ticketImportRows.join(', ');
        },
        addImportRow() {
            this.ticketImportRows.push('');
            this.$nextTick(() => {
                const inputs = this.$el.querySelectorAll('.ti-row-input input');
                if (inputs.length) inputs[inputs.length - 1].focus();
            });
        },
        splitOnCommaKeydown(rowIndex) {
            const val = this.ticketImportRows[rowIndex] || '';
            if (val.trim()) {
                // keep the current value, add blank row after
                this.ticketImportRows.splice(rowIndex + 1, 0, '');
                this.$nextTick(() => {
                    const inputs = this.$el.querySelectorAll('.ti-row-input input');
                    if (inputs[rowIndex + 1]) inputs[rowIndex + 1].focus();
                });
            }
        },
        async processImportRows() {
            const gi  = this.importGroupIndex;
            const grp = this.form.company_raffle_groups[gi];
            if (!grp || !grp.raffle_group_id) return;

            const groupName  = this.getRaffleGroupName(grp.raffle_group_id);
            let rawTickets   = this.ticketImportRows.map(r => String(r).trim()).filter(r => r.length > 0);

            if (rawTickets.length === 0) return;

            this.ticketImportProcessing = true;

            // ── 1. Track duplicates inside the input itself ─────────────────
            const seenInFile   = new Map(); // ticket_num → count
            rawTickets.forEach(n => seenInFile.set(n, (seenInFile.get(n) || 0) + 1));
            const inFileDupes  = [...seenInFile.entries()]
                .filter(([, cnt]) => cnt > 1)
                .map(([n]) => n);
            rawTickets = [...seenInFile.keys()]; // deduplicated

            // ── 2. Tickets already used by this company (DB + staged) ────────
            const existingNums = (this.form.company_tickets || []).map(t => String(t.ticket_num));
            const stagedNums   = (this.form.temp_company_tickets || []).map(t => String(t.ticket_num));
            const usedNums     = new Set([...existingNums, ...stagedNums]);

            const localDupes = rawTickets.filter(n => usedNums.has(n));
            const fresh      = rawTickets.filter(n => !usedNums.has(n));

            if (fresh.length === 0) {
                const allDupes = [...new Set([...inFileDupes, ...localDupes])];
                this.ticketImportProcessing = false;
                Swal.fire({
                    title: 'All Duplicates',
                    html: `All <b>${rawTickets.length}</b> ticket(s) already exist in this company.<br><br>`
                        + `<div style="max-height:150px;overflow-y:auto;text-align:left;font-size:0.82rem;">`
                        + allDupes.map(n => `<span style="display:inline-block;background:#ef5350;color:#fff;border-radius:4px;padding:1px 6px;margin:2px;">${n}</span>`).join(' ')
                        + `</div>`,
                    icon: 'warning',
                    confirmButtonText: 'OK',
                });
                return;
            }

            // ── 3. Check against the group quota ─────────────────────────────
            const quota         = parseInt(grp.no_of_tickets) || 0;
            const alreadyUsed   = this.existingCountForGroup(grp.raffle_group_id)
                                + this.stagedCountForGroup(grp.raffle_group_id);
            const slotsLeft     = Math.max(0, quota - alreadyUsed);
            const toAdd         = quota > 0 && fresh.length > slotsLeft ? fresh.slice(0, slotsLeft) : fresh;
            const overQuotaList = quota > 0 && fresh.length > slotsLeft ? fresh.slice(slotsLeft) : [];

            if (toAdd.length === 0) {
                this.ticketImportProcessing = false;
                Swal.fire('Quota Full',
                    `The quota for <b>${groupName}</b> (${quota}) is already full.<br>Increase "No. of Tickets" first.`,
                    'warning');
                return;
            }

            // ── 4. Server-side cross-company collision check ──────────────────
            const checkPayload = {
                url: 'checkCompanyTicketExists',
                company_tickets: toAdd,
                company_id: this.form.id || 0,
                event_id: this.GET_EVENT_ID,
            };
            let serverDupes = [];
            try {
                const checkRes = await this.$store.dispatch('urlPost', checkPayload);
                if (checkRes.code != 200) serverDupes = checkRes.existing_tickets || [];
            } catch (_) { /* non-fatal */ }

            const finalTickets    = toAdd.filter(n => !serverDupes.includes(n));
            const serverDupeList  = toAdd.filter(n =>  serverDupes.includes(n));

            if (finalTickets.length === 0) {
                this.ticketImportProcessing = false;
                Swal.fire({
                    title: 'All Duplicates',
                    html: `All tickets are already assigned to other companies in this event.<br><br>`
                        + `<div style="max-height:150px;overflow-y:auto;text-align:left;font-size:0.82rem;">`
                        + serverDupeList.map(n => `<span style="display:inline-block;background:#ef5350;color:#fff;border-radius:4px;padding:1px 6px;margin:2px;">${n}</span>`).join(' ')
                        + `</div>`,
                    icon: 'warning',
                    confirmButtonText: 'OK',
                });
                return;
            }

            // ── 5. Stage the clean tickets ────────────────────────────────────
            finalTickets.forEach(ticketNum => {
                this.form.temp_company_tickets.push({
                    ticket_num:      ticketNum,
                    raffle_group_id: grp.raffle_group_id,
                });
            });

            // Auto-expand quota if importing more than allocated
            if (quota === 0 || alreadyUsed + finalTickets.length > quota) {
                grp.no_of_tickets = alreadyUsed + finalTickets.length;
            }

            // ── 6. Build summary Swal showing every skipped ticket ────────────
            const allSkipped = [
                ...inFileDupes.map(n  => ({ num: n, reason: 'duplicate in file'           })),
                ...localDupes.map(n   => ({ num: n, reason: 'already in this company'      })),
                ...serverDupeList.map(n => ({ num: n, reason: 'used by another company'    })),
                ...overQuotaList.map(n  => ({ num: n, reason: `exceeded quota (${quota})`  })),
            ];

            let html = `<b>${finalTickets.length}</b> ticket(s) staged for <b>${groupName}</b>.`;

            if (allSkipped.length > 0) {
                html += `<br><br><b style="color:#ef5350">${allSkipped.length} ticket(s) skipped:</b>`
                    + `<div style="max-height:200px;overflow-y:auto;border:1px solid rgba(255,255,255,0.15);border-radius:6px;padding:6px;margin-top:6px;text-align:left;font-size:0.82rem;">`;
                allSkipped.forEach(({ num, reason }) => {
                    html += `<div style="display:flex;gap:6px;align-items:center;margin:2px 0;">`
                          + `<span style="background:#ef5350;color:#fff;border-radius:4px;padding:1px 7px;font-weight:600;min-width:70px;text-align:center;">${num}</span>`
                          + `<span style="opacity:0.75">${reason}</span>`
                          + `</div>`;
                });
                html += `</div>`;
            }

            this.ticketImportProcessing = false;
            this.ticketImportDialog     = false;
            this.ticketImportInput      = '';
            this.ticketImportRows       = [];

            Swal.fire({
                title: 'Import Complete',
                html,
                icon: finalTickets.length > 0 ? 'success' : 'warning',
                confirmButtonText: 'OK',
                width: allSkipped.length > 0 ? 520 : 400,
            });
        },
        downloadGroupTemplate(groupIndex) {
            const grp       = this.form.company_raffle_groups[groupIndex];
            const groupName = grp?.raffle_group_id ? this.getRaffleGroupName(grp.raffle_group_id) : 'Group';
            // Use cell objects with type 's' (string) so XLSX preserves leading zeros
            const ws        = XLSX.utils.aoa_to_sheet([
                ['TICKET_NUM'],
                [{ t: 's', v: '0001' }],
                [{ t: 's', v: '0002' }],
                [{ t: 's', v: '0003' }],
            ]);
            ws['!cols'] = [{ wch: 18 }];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Tickets');

            // Instructions sheet
            const instructions = XLSX.utils.aoa_to_sheet([
                ['Raffle Ticket Import Instructions'],
                [''],
                ['Column', 'Description'],
                ['TICKET_NUM', 'The ticket number to assign (one per row).'],
                [''],
                ['Notes:'],
                ['- The header row must be included.'],
                ['- Ticket numbers must be unique within the event.'],
                [`- This template is for Raffle Group: ${groupName}`],
            ]);
            instructions['!cols'] = [{ wch: 20 }, { wch: 60 }];
            XLSX.utils.book_append_sheet(wb, instructions, 'Instructions');

            const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob   = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url    = window.URL.createObjectURL(blob);
            const link   = document.createElement('a');
            link.href    = url;
            link.setAttribute('download', `Ticket_Import_Template_${groupName.replace(/\s+/g,'_')}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        },

        setDefaults() {
            this.generate_raffle_group_id = null;
        }
    },
    watch: {
        DIALOGS: {
            handler(val) {
                if (!val) {
                    this.initialForm();
                    this.$store.commit('DIALOG_VIEW', {});
                } else {
                    this.getMaxTicketNum();
                }
            },
        },
        'settings.disable_company_editing': {
            handler(val) {
                if (val === 1 && this.DIALOGS && this.ACTION === 'Update') {
                    Swal.fire({
                        title: 'Company Editing Disabled',
                        text: 'Company editing is currently not available. Please contact your administrator.',
                        icon: 'info',
                        confirmButtonText: 'OK',
                        allowOutsideClick: false,
                    }).then(() => {
                        this.closeDialog();
                    });
                }
            },
        },
    },
};
</script>

<style scoped>
/* ── Card ── */
.co-card {
    background: #1a1e2e !important;
    border-radius: 12px !important;
}

/* ── Header ── */
.co-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    background: linear-gradient(135deg, #1565c0 0%, #0d47a1 60%, #1a237e 100%);
}
.co-header-left {
    display: flex;
    align-items: center;
    gap: 4px;
}
.co-header-title {
    color: #fff;
    font-size: 0.95rem;
    font-weight: 700;
    letter-spacing: 0.4px;
    line-height: 1.2;
}
.co-header-sub {
    color: rgba(255,255,255,0.6);
    font-size: 0.72rem;
    letter-spacing: 0.3px;
}
.gap-8 { gap: 8px; }
.co-icon-btn { opacity: 0.85; }
.co-icon-btn:hover { opacity: 1; }

/* ── Body ── */
.co-body { background: #1a1e2e !important; }

/* ── Section labels ── */
.co-section-label {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #7eb3ff;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    opacity: 0.85;
}

/* ── Fields ── */
.co-field .v-input__slot { background: rgba(255,255,255,0.05) !important; }
.co-field .v-input__slot fieldset { border-color: rgba(126,179,255,0.22) !important; }
.co-field.v-input--is-focused .v-input__slot fieldset { border-color: #7eb3ff !important; }
.co-field input, .co-field .v-select__selection { color: #e8eaf6 !important; font-size: 0.88rem; }
.co-field .v-label { color: rgba(200,210,255,0.45) !important; font-size: 0.85rem; }
.co-field.v-input--is-focused .v-label { color: #7eb3ff !important; }

/* ── Unique code hint (Submit mode) ── */
.co-unique-code-hint {
    font-size: 0.78rem;
    color: rgba(200,210,255,0.65);
    background: rgba(126,179,255,0.07);
    border: 1px solid rgba(126,179,255,0.18);
    border-radius: 7px;
    padding: 7px 12px;
    display: flex;
    align-items: center;
    margin-bottom: 8px;
}
.co-gen-unique-btn {
    background: linear-gradient(135deg, #1a237e, #283593) !important;
    color: #7eb3ff !important;
    border: 1px solid rgba(126,179,255,0.3) !important;
    border-radius: 7px !important;
    text-transform: none !important;
    font-weight: 600;
    font-size: 0.78rem;
    white-space: nowrap;
    flex-shrink: 0;
}

/* ── Raffle row ── */
.co-raffle-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
}
.co-checkbox .v-label { display: none; }
.co-checkbox-label { font-size: 0.85rem; color: rgba(220,230,255,0.8); font-weight: 500; }
.co-tickets-field { max-width: 130px; }
.co-tickets-readonly { font-size: 0.82rem; color: rgba(200,210,255,0.6); display: flex; align-items: center; gap: 4px; }
.co-gen-btn {
    background: linear-gradient(135deg, #1565c0, #0d47a1) !important;
    color: #fff !important;
    border-radius: 7px !important;
    text-transform: none !important;
    font-weight: 600;
    font-size: 0.8rem;
    letter-spacing: 0.3px;
}

/* ── Probability ── */
.co-prob-note { font-size: 0.62rem; color: rgba(200,210,255,0.4); text-transform: none; letter-spacing: 0; font-weight: 400; }
.co-prob-row {
    display: flex;
    gap: 10px;
}
.co-prob-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border-radius: 10px;
    padding: 12px 10px;
    min-width: 0;
}
.co-prob-card--normal {
    border: 1px solid rgba(79,195,247,0.22);
    background: rgba(79,195,247,0.05);
}
.co-prob-card--major {
    border: 1px solid rgba(255,167,38,0.28);
    background: rgba(255,167,38,0.05);
}
.co-prob-card--unconfigured {
    border-color: rgba(255,167,38,0.1);
    background: rgba(255,167,38,0.02);
    opacity: 0.7;
}
.co-prob-card--won {
    border-color: rgba(76,175,80,0.35) !important;
    background: rgba(76,175,80,0.07) !important;
}
.co-won-chip {
    font-size: 0.6rem !important;
    height: 16px !important;
    padding: 0 5px !important;
}
.co-prob-card-label {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: rgba(200,210,255,0.6);
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.co-prob-gauge-wrap { flex-shrink: 0; }
.co-prob-pct-lg { font-size: 0.82rem; font-weight: 700; }
.co-prob-tickets { font-size: 0.70rem; display: flex; align-items: center; justify-content: center; gap: 3px; flex-wrap: wrap; }
.co-prob-excluded { font-size: 0.72rem; color: rgba(200,210,255,0.4); display: flex; align-items: center; font-style: italic; }

/* Per-group expansion */
.co-prob-expand { background: transparent !important; }
.co-prob-expand-panel { background: rgba(255,255,255,0.04) !important; border-radius: 8px !important; border: 1px solid rgba(126,179,255,0.12) !important; }
.co-prob-expand-header { font-size: 0.75rem; color: rgba(200,210,255,0.7); font-weight: 600; min-height: 36px !important; }
.co-prob-expand-content { padding: 4px 8px 8px !important; }
.co-prob-group-row { display: flex; align-items: center; gap: 10px; padding: 4px 0; }
.co-prob-group-name { width: 90px; font-size: 0.75rem; color: rgba(200,210,255,0.75); flex-shrink: 0; }
.co-prob-group-bar-wrap { flex: 1; }
.co-prob-group-pct { width: 40px; text-align: right; font-size: 0.73rem; color: #7eb3ff; font-weight: 600; }
.co-prob-group-tickets { width: 60px; text-align: right; font-size: 0.68rem; color: rgba(200,210,255,0.4); }

/* ── Per-group raffle table ── */
.co-group-table-wrap {
    background: rgba(126,179,255,0.04);
    border: 1px solid rgba(126,179,255,0.15);
    border-radius: 8px;
    padding: 10px 12px;
}
.co-group-table-header {
    display: grid;
    grid-template-columns: 1fr 100px 100px auto;
    gap: 8px;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: rgba(200,210,255,0.45);
    margin-bottom: 6px;
    padding: 0 2px;
}
.co-group-table-row {
    display: grid;
    grid-template-columns: 1fr 100px 80px auto;
    gap: 8px;
    align-items: center;
    margin-bottom: 6px;
}
.co-grp-select { min-width: 0; }
.co-grp-tickets { max-width: 110px; }
.co-grp-staged-count {
    font-size: 0.75rem;
    color: rgba(200,210,255,0.6);
    display: flex;
    align-items: center;
    gap: 3px;
    white-space: nowrap;
}
.co-grp-actions { display: flex; align-items: center; gap: 2px; }
.co-import-btn {
    background: linear-gradient(135deg, #2e7d32, #1b5e20) !important;
    color: #fff !important;
    border-radius: 7px !important;
    text-transform: none !important;
    font-weight: 600;
    font-size: 0.8rem;
}
.co-group-empty {
    font-size: 0.75rem;
    color: rgba(200,210,255,0.35);
    font-style: italic;
    padding: 4px 2px;
}
.co-add-group-btn {
    background: rgba(126,179,255,0.12) !important;
    color: #7eb3ff !important;
    border-radius: 6px !important;
    text-transform: none !important;
    font-weight: 600;
    font-size: 0.75rem;
    border: 1px solid rgba(126,179,255,0.25) !important;
}

/* ── Staged tickets ── */
.co-staged-wrap {
    background: rgba(76,175,80,0.06);
    border: 1px solid rgba(76,175,80,0.2);
    border-radius: 8px;
    padding: 10px 12px;
}
.co-staged-header {
    font-size: 0.75rem;
    color: #81c784;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
}
.co-staged-chips { display: flex; flex-wrap: wrap; }
.co-staged-scroll { max-height: 160px; overflow-y: auto; }
.co-staged-scroll::-webkit-scrollbar { width: 4px; }
.co-staged-scroll::-webkit-scrollbar-track { background: rgba(255,255,255,0.04); border-radius: 4px; }
.co-staged-scroll::-webkit-scrollbar-thumb { background: rgba(126,179,255,0.3); border-radius: 4px; }
.co-ticket-chip { font-size: 0.7rem !important; }
.co-ticket-group { opacity: 0.55; font-size: 0.65rem; }

/* ── Actions ── */
.co-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 20px;
    border-top: 1px solid rgba(255,255,255,0.07);
    background: #141728;
}
.co-submit-btn {
    background: linear-gradient(135deg, #1565c0, #0d47a1) !important;
    color: #fff !important;
    border-radius: 8px !important;
    text-transform: none !important;
    font-weight: 600;
    letter-spacing: 0.3px;
    padding: 0 28px !important;
}

/* ── Sub-dialogs ── */
.co-sub-card { background: #1a1e2e !important; }
.co-sub-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: linear-gradient(135deg, #1565c0, #0d47a1);
    color: #fff;
    font-size: 0.9rem;
    font-weight: 700;
}
.co-action-btn {
    border-radius: 7px !important;
    text-transform: none !important;
    font-weight: 600;
    color: #fff !important;
    font-size: 0.8rem;
}
.co-table { background: transparent !important; }
.co-table th { background: rgba(255,255,255,0.05) !important; color: rgba(200,210,255,0.7) !important; font-size: 0.75rem !important; }
.co-table td { color: rgba(220,230,255,0.8) !important; font-size: 0.82rem !important; border-bottom: 1px solid rgba(255,255,255,0.05) !important; }

/* ── Ticket import dialog rows ── */
.ti-rows-wrap {
    max-height: 320px;
    overflow-y: auto;
    padding: 2px 0;
    border: 1px solid rgba(126,179,255,0.15);
    border-radius: 8px;
    padding: 6px 8px;
    background: rgba(0,0,0,0.15);
}
.ti-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
}
.ti-row-num {
    min-width: 22px;
    text-align: right;
    font-size: 0.72rem;
    color: rgba(200,210,255,0.35);
    user-select: none;
}
.ti-row-input { flex: 1; }
.ti-row-input .v-input__slot { min-height: 32px !important; padding: 0 8px !important; }
.ti-row-input input { font-size: 0.85rem !important; padding: 4px 0 !important; }
</style>
